import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Check, Lock, Pencil, Play, RotateCcw, Save, Wand2, X } from 'lucide-react';
import { RichMarkdown } from '../pages/design/RichMarkdown';
import { hasBuiltRevision as hasBuiltRevisionOf, savedPlanDiffersFromBuilt } from './plan-state';
import type { PlanArtifactSource } from '@volley/crucible-ipc-types/build-game-types';

type PlanWorkspaceTab = 'answers' | 'plan' | 'notes';
type PlanWorkspaceSurface = 'plan' | 'design';
type PrimaryActionKind =
  | 'back-to-chat'
  | 'draft-plan'
  | 'retry-build'
  | 'rebuild-from-plan'
  | 'built-current'
  | 'start-building';

export interface PlanWorkspaceProps {
  gameId: string;
  gameName: string;
  plan: string;
  answeredCount?: number;
  totalCount?: number;
  planRevision?: number;
  planHash?: number;
  planUpdatedAt?: string;
  planSource?: PlanArtifactSource;
  surface?: PlanWorkspaceSurface;
  showInterviewAnswers?: boolean;
  lastBuiltPlanRevision?: number;
  lastBuiltPlanHash?: number;
  buildComplete?: boolean;
  buildCompletionUnverified?: boolean;
  buildRequestPending?: boolean;
  /** When the design was last sent to the build (ISO) — shown on the built status row. */
  lastBuiltAt?: string;
  busy?: boolean;
  draftPlanPending?: boolean;
  onSavePlan: (plan: string) => Promise<void>;
  onStartBuilding: () => void;
  onDraftPlan?: () => void;
  onRebuildFromPlan: () => void;
  onBackToChat?: () => void;
  onSubmitRefinement: (text: string) => void;
  onStartOver: () => void;
}

function revisionLabel(revision: number | undefined): string {
  return revision === undefined ? 'Draft — not saved yet' : `Revision ${revision}`;
}

export function PlanWorkspace({
  gameId,
  gameName,
  plan,
  answeredCount,
  totalCount,
  planRevision,
  planHash,
  planUpdatedAt,
  planSource,
  surface = 'plan',
  showInterviewAnswers,
  lastBuiltPlanRevision,
  lastBuiltPlanHash,
  buildComplete = false,
  buildCompletionUnverified = false,
  buildRequestPending = false,
  lastBuiltAt,
  busy = false,
  draftPlanPending = false,
  onSavePlan,
  onStartBuilding,
  onDraftPlan,
  onRebuildFromPlan,
  onBackToChat,
  onSubmitRefinement,
  onStartOver,
}: PlanWorkspaceProps): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<PlanWorkspaceTab>('plan');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(plan);
  const [saving, setSaving] = useState(false);
  const [refineText, setRefineText] = useState('');
  const revisionLocked = buildRequestPending;
  const hasInterviewAnswers =
    showInterviewAnswers ?? (answeredCount !== undefined || totalCount !== undefined);
  const tabs = useMemo(
    () =>
      [
        ...(hasInterviewAnswers ? ([['answers', 'Interview Answers']] as const) : []),
        ['plan', 'Build Plan'],
        ['notes', 'Build Notes'],
      ] as const,
    [hasInterviewAnswers],
  );

  useEffect(() => {
    if (!editing) setDraft(plan);
  }, [editing, plan]);

  useEffect(() => {
    if (activeTab === 'answers' && !hasInterviewAnswers) setActiveTab('plan');
  }, [activeTab, hasInterviewAnswers]);

  const dirty = draft !== plan;
  const hasPlan = plan.trim().length > 0;
  const comparison = { planRevision, planHash, lastBuiltPlanRevision, lastBuiltPlanHash };
  const hasBuiltRevision = hasBuiltRevisionOf(comparison);
  const savedRevisionDiffers = savedPlanDiffersFromBuilt(comparison);
  const primaryActionKind: PrimaryActionKind = buildCompletionUnverified
    ? 'retry-build'
    : buildRequestPending
      ? 'back-to-chat'
      : !hasPlan && onDraftPlan
        ? 'draft-plan'
        : hasBuiltRevision && savedRevisionDiffers
          ? 'rebuild-from-plan'
          : hasBuiltRevision
            ? 'built-current'
            : 'start-building';
  const primaryBusy =
    primaryActionKind === 'draft-plan'
      ? draftPlanPending
      : busy && primaryActionKind !== 'back-to-chat';
  const primaryDisabled =
    primaryBusy || (editing && !revisionLocked) || (primaryActionKind !== 'draft-plan' && !hasPlan);
  const builtRowDetail = useMemo(() => {
    const parts = [`plan revision ${lastBuiltPlanRevision ?? '—'}`];
    if (lastBuiltAt) {
      const at = new Date(lastBuiltAt);
      if (!Number.isNaN(at.getTime())) {
        const prefix = buildComplete ? 'finished' : 'sent';
        parts.push(
          `${prefix} ${at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`,
        );
      }
    }
    return parts.join(' · ');
  }, [buildComplete, lastBuiltAt, lastBuiltPlanRevision]);
  const metadata = useMemo(() => {
    const parts = [revisionLabel(planRevision)];
    if (planUpdatedAt) parts.push(new Date(planUpdatedAt).toLocaleString());
    if (planSource) parts.push(planSource);
    return parts.join(' · ');
  }, [planRevision, planSource, planUpdatedAt]);

  async function handleSave(): Promise<void> {
    if (revisionLocked) return;
    setSaving(true);
    try {
      await onSavePlan(draft);
      setEditing(false);
    } catch {
      // Workspace owns the visible save-error copy. Keep the user's draft
      // mounted so a failed IPC save cannot hide unsaved edits.
    } finally {
      setSaving(false);
    }
  }

  function handleCancel(): void {
    if (dirty && !window.confirm('Discard unsaved plan changes?')) return;
    setDraft(plan);
    setEditing(false);
  }

  function handleRefine(e: FormEvent): void {
    e.preventDefault();
    if (revisionLocked) return;
    const trimmed = refineText.trim();
    if (!trimmed) return;
    onSubmitRefinement(trimmed);
    setRefineText('');
  }

  return (
    <section
      data-testid="plan-workspace"
      data-game-id={gameId}
      data-surface={surface}
      className="flex flex-1 flex-col overflow-hidden bg-crucible-surface"
    >
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-white">Build Plan</h2>
          <p className="mt-0.5 text-xs text-white/40">{metadata}</p>
        </div>
        <button
          type="button"
          onClick={onStartOver}
          disabled={revisionLocked}
          className="text-xs text-white/40 underline-offset-2 hover:text-white/70 hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-white/40 disabled:hover:no-underline"
        >
          Start over
        </button>
      </header>

      <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
        <div className="flex rounded-md border border-white/10 bg-white/[0.03] p-0.5">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className="rounded px-3 py-1.5 text-xs font-medium text-white/50 data-[active=true]:bg-white/10 data-[active=true]:text-white"
              data-active={activeTab === id ? 'true' : 'false'}
            >
              {label}
            </button>
          ))}
        </div>
        {activeTab === 'plan' && (
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                {dirty && <span className="text-xs text-amber-200/80">Unsaved changes</span>}
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving || revisionLocked}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/60 hover:text-white disabled:opacity-40"
                >
                  <X size={13} />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={!dirty || saving || revisionLocked}
                  className="inline-flex items-center gap-1.5 rounded-md bg-crucible-accent px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Save size={13} />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (!revisionLocked) setEditing(true);
                }}
                disabled={revisionLocked}
                className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-white/60"
              >
                <Pencil size={13} />
                Edit
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {activeTab === 'answers' && (
          <div className="max-w-3xl space-y-4 text-sm text-white/70">
            <h3 className="text-base font-semibold text-white">{gameName}</h3>
            <p>
              Discovery answers are captured for this game. {answeredCount ?? 0}/{totalCount ?? 0}{' '}
              questions answered.
            </p>
          </div>
        )}
        {activeTab === 'plan' && (
          <div className="max-w-5xl">
            {revisionLocked && buildCompletionUnverified && (
              <div
                data-testid="plan-completion-unverified-banner"
                className="mb-4 flex items-center gap-3 rounded-lg border border-amber-300/25 bg-amber-300/[0.06] px-4 py-3"
              >
                <RotateCcw size={15} className="shrink-0 text-amber-200" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-amber-100">
                    Build completion could not be verified
                  </p>
                  <p className="mt-0.5 text-xs text-amber-200/75">
                    Retry build from this plan so Foundry can verify before play or deploy.
                  </p>
                </div>
              </div>
            )}
            {revisionLocked && !buildCompletionUnverified && (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3">
                <Lock size={15} className="shrink-0 text-white/45" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-white/80">
                    Plan locked while the build runs
                  </p>
                  <p className="mt-0.5 text-xs text-white/45">
                    Design docs stay editable — your changes queue as design changes for after the
                    build.
                  </p>
                </div>
              </div>
            )}
            {!revisionLocked && primaryActionKind === 'rebuild-from-plan' && (
              <div
                data-testid="plan-pending-banner"
                className="mb-4 flex items-center gap-3 rounded-lg border border-amber-300/25 bg-amber-300/[0.06] px-4 py-3"
              >
                <RotateCcw size={15} className="shrink-0 text-amber-200" aria-hidden="true" />
                <p className="text-xs text-amber-200">
                  This plan has saved changes that haven&rsquo;t been built. Rebuild to bring the
                  game up to date.
                </p>
              </div>
            )}
            {editing ? (
              <textarea
                aria-label="Game plan markdown"
                value={draft}
                onChange={(e) => setDraft(e.currentTarget.value)}
                disabled={revisionLocked}
                className="min-h-[55vh] w-full resize-none rounded-lg border border-white/10 bg-black/20 p-4 font-mono text-sm leading-relaxed text-white outline-none focus:border-crucible-accent/70 disabled:cursor-not-allowed disabled:opacity-60"
                spellCheck={false}
              />
            ) : hasPlan ? (
              <div data-testid="plan-workspace-body">
                <RichMarkdown content={plan} />
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm text-white/40">No plan has been saved yet.</p>
                {onDraftPlan && (
                  <button
                    type="button"
                    data-testid="plan-empty-draft"
                    onClick={onDraftPlan}
                    disabled={primaryDisabled}
                    className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-white/70 hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Wand2 size={13} aria-hidden="true" />
                    Draft Build Plan
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === 'notes' && (
          <div className="max-w-3xl space-y-4 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Check size={14} className="text-emerald-300" />
              <span>Last built: {revisionLabel(lastBuiltPlanRevision)}</span>
            </div>
            {savedRevisionDiffers && (
              <div className="flex items-center gap-2 text-amber-200">
                <RotateCcw size={14} />
                <span>Saved plan changes have not been rebuilt yet.</span>
              </div>
            )}
          </div>
        )}
      </div>

      <footer className="border-t border-white/10 px-6 py-4">
        {primaryActionKind === 'back-to-chat' && (
          <button
            type="button"
            data-testid="plan-back-to-chat"
            onClick={onBackToChat}
            disabled={onBackToChat === undefined}
            className="w-full rounded-lg border border-white/10 px-4 py-2.5 text-sm font-medium text-white/70 hover:bg-white/[0.04] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Back to Build for live progress
          </button>
        )}
        {primaryActionKind === 'start-building' && (
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="plan-primary-start"
              onClick={onStartBuilding}
              disabled={primaryDisabled}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Play size={14} aria-hidden="true" />
              {primaryBusy ? 'Working...' : 'Start Building'}
            </button>
            <p className="text-xs leading-relaxed text-white/40">
              Hands this plan to the build agent. Design docs stay editable while it runs.
            </p>
          </div>
        )}
        {primaryActionKind === 'draft-plan' && onDraftPlan && (
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="plan-primary-draft"
              onClick={onDraftPlan}
              disabled={primaryDisabled}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Wand2 size={14} aria-hidden="true" />
              {primaryBusy ? 'Drafting...' : 'Draft Build Plan'}
            </button>
            <p className="text-xs leading-relaxed text-white/40">
              Creates the build plan from the current design docs.
            </p>
          </div>
        )}
        {primaryActionKind === 'rebuild-from-plan' && (
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="plan-primary-rebuild"
              onClick={onRebuildFromPlan}
              disabled={primaryDisabled}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={14} aria-hidden="true" />
              {primaryBusy ? 'Working...' : 'Rebuild from Plan'}
            </button>
            <p className="text-xs leading-relaxed text-white/40">
              Saved plan ({revisionLabel(planRevision).toLowerCase()}) differs from the last built
              plan ({revisionLabel(lastBuiltPlanRevision).toLowerCase()}).
            </p>
          </div>
        )}
        {primaryActionKind === 'retry-build' && (
          <div className="flex items-center gap-4">
            <button
              type="button"
              data-testid="plan-primary-retry-build"
              onClick={onRebuildFromPlan}
              disabled={primaryDisabled}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-crucible-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={14} aria-hidden="true" />
              {primaryBusy ? 'Working...' : 'Retry build'}
            </button>
            <p className="text-xs leading-relaxed text-white/40">
              Reruns the current plan so Foundry can verify the build before play or deploy.
            </p>
          </div>
        )}
        {primaryActionKind === 'built-current' && (
          <div
            data-testid="plan-built-status-row"
            className="flex items-center gap-3 rounded-lg border border-emerald-300/20 bg-emerald-300/[0.05] px-4 py-2.5"
          >
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-emerald-300/15">
              <Check size={13} className="text-emerald-300" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-300">
                {buildComplete ? 'Built from this plan' : 'Sent to build from this plan'}
              </p>
              <p className="text-xs text-white/40">{builtRowDetail}</p>
            </div>
          </div>
        )}
        {!revisionLocked && (
          <form onSubmit={handleRefine} className="mt-4 flex gap-2">
            <input
              value={refineText}
              onChange={(e) => setRefineText(e.currentTarget.value)}
              placeholder="Ask for a plan edit..."
              className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/25 focus:border-crucible-accent/70"
            />
            <button
              type="submit"
              disabled={busy || refineText.trim().length === 0}
              className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Send
            </button>
          </form>
        )}
      </footer>
    </section>
  );
}
